import classNames from "classnames";
import { FC, ReactNode } from "react";

import styles from "./TransportTimetable.module.scss";
import { Chip } from "@livingmap/core-ui-v2";
import { TransportIdentifier } from "../../hooks/useTransportService";
import { generateTextColourFromBackgroundColour } from "../../utils/textColour";
import NoDepartures from "../NoDepartures/NoDepartures";
import { useTranslation } from "react-i18next";

export type TransportTimetableDeparture = {
  transportIdentifier: TransportIdentifier;
  destination: string;
  departureTime: string;
  departureTimeISO: string;
};

interface Props {
  dataQA: string;
  className?: string;
  departures: TransportTimetableDeparture[];
  onDepartureClick?: (transportIdentifier: TransportIdentifier) => void;
}

const truncateChipText = (text: string) =>
  text.length > 6 ? `${text.slice(0, 6)}...` : text;

const generateDepartureListItems = (
  departures: TransportTimetableDeparture[],
  onDepartureClick?: (transportIdentifier: TransportIdentifier) => void,
): ReactNode[] => {
  return departures.map((departure, index) => (
    <li key={index} className={styles.row}>
      <div
        className={styles.depatureLine}
        onClick={() =>
          onDepartureClick && onDepartureClick(departure.transportIdentifier)
        }
      >
        <Chip
          dataQA="departure-chip"
          className={styles.chip}
          onClick={() => {}}
          children={truncateChipText(departure.transportIdentifier.code)}
          style={{
            backgroundColor: departure.transportIdentifier.colour,
            color: generateTextColourFromBackgroundColour(
              departure.transportIdentifier.colour,
            ),
          }}
          active
        />
      </div>
      <div className={styles.departureLabel}>
        <span className={styles.destination}>{departure.destination}</span>
      </div>
      <div className={styles.departureTime}>
        <span className={styles.text}>{departure.departureTime}</span>
      </div>
    </li>
  ));
};

const TransportTimetable: FC<Props> = ({
  departures,
  className,
  dataQA,
  onDepartureClick,
}) => {
  const { t } = useTranslation();
  const departureListItems = generateDepartureListItems(
    departures,
    onDepartureClick,
  );
  const hasDepartures = departureListItems.length > 0;

  return (
    <ul
      data-qa={dataQA}
      className={classNames(styles.container, className, {
        [styles.emptyState]: !hasDepartures,
      })}
    >
      {hasDepartures ? (
        departureListItems
      ) : (
        <li className={styles.emptyStateMessage}>
          <NoDepartures title={t("poi.departures.no_upcoming_departures")} />
        </li>
      )}
    </ul>
  );
};

export default TransportTimetable;
